import React, { useState, useEffect } from 'react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Plus, AlertTriangle, CheckCircle, Loader2, AlertCircle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogScrollArea,
  DialogFooter,
} from '@/components/ui/dialog';
import { motion } from 'framer-motion';
import { CustomGPTSuccessModalContent } from './CustomGPTSuccessModal';
import { useTranslation } from 'react-i18next';
import { AdvancedSocketSelection } from '../AdvancedSocketSelection';
import { integrationRegistry } from '../../../integrations/registry';
import { SpaceSocket } from '../../../services/api';
import { getOrCreateDefaultSocket } from '../../../utils/socketUtils';
import { buildBindingPermissions } from '../shared/bindingPayload';
import { MemoryPermissionsFields } from '../shared/MemoryPermissionsFields';
import { ContentFiltersFields } from '../shared/ContentFiltersFields';
import { RateLimitFields } from '../shared/RateLimitFields';

interface CustomGPTIntegrationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreateIntegration: (integration: any) => Promise<any>;
  socket?: SpaceSocket;
  spaceId: string;
}

export default function CustomGPTIntegrationModal({ 
  isOpen, 
  onClose, 
  onCreateIntegration,
  socket,
  spaceId
}: CustomGPTIntegrationModalProps) {
  const { t } = useTranslation();
  const [integrationName, setIntegrationName] = useState('');
  const [description, setDescription] = useState('');
  const [responseFormat, setResponseFormat] = useState<'rich' | 'simple'>('rich');
  const [allowFilterOverride, setAllowFilterOverride] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [createdIntegration, setCreatedIntegration] = useState<any>(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [selectedSocket, setSelectedSocket] = useState<SpaceSocket | null>(socket || null);
  const [socketLoading, setSocketLoading] = useState(false);
  const [socketError, setSocketError] = useState<string | null>(null);
  
  // Permission states
  const [canRead, setCanRead] = useState(true);
  const [canWrite, setCanWrite] = useState(false);
  const [canSearch, setCanSearch] = useState(true);
  const [canDelete, setCanDelete] = useState(false);
  
  // Content filtering
  const [categories, setCategories] = useState('');
  const [excludeTags, setExcludeTags] = useState('');
  const [minImportance, setMinImportance] = useState(1);
  
  // Rate limiting
  const [requestsPerHour, setRequestsPerHour] = useState(1000);

  // Get or create default socket when modal opens
  useEffect(() => {
    if (isOpen && !socket && spaceId) {
      const loadDefaultSocket = async () => {
        setSocketLoading(true);
        setSocketError(null);
        try {
          const defaultSocket = await getOrCreateDefaultSocket(spaceId, integrationRegistry.custom_gpt_action);
          setSelectedSocket(defaultSocket);
        } catch (error) {
          console.error('Failed to get or create default socket:', error);
          setSocketError(error instanceof Error ? error.message : 'Failed to load socket');
        } finally {
          setSocketLoading(false);
        }
      };
      loadDefaultSocket();
    } else if (socket) {
      setSelectedSocket(socket);
    }
  }, [isOpen, socket, spaceId]);

  // Check template compatibility
  const socketTemplateId = selectedSocket?.socket_config?.template_id;
  const isCompatibleTemplate = !socketTemplateId || socketTemplateId === 'mcp' || socketTemplateId === 'api';
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!integrationName.trim() || !selectedSocket) return;

    // Validate template compatibility
    if (!isCompatibleTemplate) {
      alert(t('customGPT.templateCompatibilityError', { ns: 'integrations' }) as string);
      return;
    }

    setIsCreating(true);
    try {
      const permissions = buildBindingPermissions({
        flags: {
          can_read: canRead,
          can_write: canWrite,
          can_search: canSearch,
          can_delete: canDelete,
        },
        categoriesComma: categories,
        excludeTagsComma: excludeTags,
        minImportance,
        requestsPerHour,
      });

      const auth_config = {
        response_format: responseFormat,
        allow_filter_override: allowFilterOverride,
        description: description.trim() || undefined
      };

      const integrationData = {
        integration_type: 'custom_gpt_action',
        integration_name: integrationName.trim(),
        permissions,
        auth_config,
        socket_id: selectedSocket.socket_id
      };

      const createdIntegration = await onCreateIntegration(integrationData);
      
      // Store the created integration and show success modal
      setCreatedIntegration({
        integration_name: integrationData.integration_name,
        auth_config: {
          api_key: createdIntegration.auth_config.api_key, // Real API key from backend
          response_format: integrationData.auth_config.response_format,
          allow_filter_override: integrationData.auth_config.allow_filter_override
        },
        permissions: integrationData.permissions
      });
      setShowSuccessModal(true);
      
    } catch (error) {
      console.error('Failed to create Custom GPT integration:', error);
      // Show error to user
      alert(t('customGPT.creationError', { ns: 'integrations' }) as string);
    } finally {
      setIsCreating(false);
    }
  };

  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
    setCreatedIntegration(null);
    
    // Reset form
    setIntegrationName('');
    setDescription('');
    setResponseFormat('rich');
    setAllowFilterOverride(false);
    setCanRead(true);
    setCanWrite(false);
    setCanSearch(true);
    setCanDelete(false);
    setCategories('');
    setExcludeTags('');
    setMinImportance(1);
    setRequestsPerHour(1000);
    
    onClose();
  };

  const handleDialogOpenChange = (open: boolean) => {
    if (!open) {
      if (showSuccessModal) {
        handleSuccessModalClose();
      } else {
        onClose();
      }
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleDialogOpenChange}>
      <DialogContent
        variant="scrollable"
        className="max-w-5xl bg-white"
      >
        {showSuccessModal && createdIntegration ? (
          <CustomGPTSuccessModalContent
            integration={createdIntegration}
            onClose={handleSuccessModalClose}
            spaceName={
              selectedSocket?.socket_name ||
              (t('customGPT.defaultSpaceName', { ns: 'integrations' }) as string)
            }
          />
        ) : (
          <>
            <DialogHeader className="flex-shrink-0 p-6 pb-4 pr-12">
              <DialogTitle className="text-xl font-semibold text-gray-900">Add Custom GPT Integration</DialogTitle>
              <DialogDescription className="text-gray-600">
                {selectedSocket ? (
                  <>Create a new Custom GPT integration for <span className="font-medium">{selectedSocket.socket_name}</span></>
                ) : (
                  'Setting up your integration...'
                )}
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
              <DialogScrollArea className="space-y-6 px-6">
              {/* Integration Name */}
              <div className="space-y-2">
                <label className="text-gray-700 text-sm font-medium">
                  Integration Name *
                </label>
                <Input
                  value={integrationName}
                  onChange={(e) => setIntegrationName(e.target.value)}
                  placeholder="e.g., Work Assistant GPT"
                  className="bg-white border-gray-300 text-gray-900"
                  required
                />
              </div>

              {/* Description */}
              <div className="space-y-2">
                <label className="text-gray-700 text-sm font-medium">
                  Description
                </label>
                <Input
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder={t('customGPT.descriptionPlaceholder', { ns: 'integrations' }) as string}
                  className="bg-white border-gray-300 text-gray-900"
                />
              </div>

              {/* Response Format */}
              <div className="space-y-3">
                <label className="text-gray-700 text-sm font-medium">
                  Response Format
                </label>
                <div className="flex space-x-4">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      name="responseFormat"
                      value="rich"
                      checked={responseFormat === 'rich'}
                      onChange={(e) => setResponseFormat(e.target.value as 'rich')}
                      className="text-indigo-600 focus:ring-indigo-500"
                    />
                    <span className="text-gray-700">Rich (includes metadata, relevance scores)</span>
                  </label>
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      name="responseFormat"
                      value="simple"
                      checked={responseFormat === 'simple'}
                      onChange={(e) => setResponseFormat(e.target.value as 'simple')}
                      className="text-indigo-600 focus:ring-indigo-500"
                    />
                    <span className="text-gray-700">Simple (content and tags only)</span>
                  </label>
                </div>
              </div>

              <MemoryPermissionsFields
                value={{
                  can_read: canRead,
                  can_write: canWrite,
                  can_search: canSearch,
                  can_delete: canDelete,
                }}
                onChange={(v) => {
                  setCanRead(v.can_read);
                  setCanWrite(v.can_write);
                  setCanSearch(v.can_search);
                  setCanDelete(v.can_delete);
                }}
              />

              <div className="space-y-4">
                <ContentFiltersFields
                  categories={categories}
                  excludeTags={excludeTags}
                  minImportance={minImportance}
                  onCategoriesChange={setCategories}
                  onExcludeTagsChange={setExcludeTags}
                  onMinImportanceChange={setMinImportance}
                />
                <RateLimitFields
                  requestsPerHour={requestsPerHour}
                  onRequestsPerHourChange={setRequestsPerHour}
                  helperKey="customGPT"
                />
              </div>

              {/* Advanced Options */}
              <div className="space-y-3">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={allowFilterOverride}
                    onChange={(e) => setAllowFilterOverride(e.target.checked)}
                    className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <span className="text-gray-700 font-medium">Allow GPT to override filter options</span>
                </label>
                <p className="text-gray-500 text-sm ml-6">
                  When enabled, the GPT can specify additional filters in search requests beyond the base filters configured above.
                </p>
              </div>

              {/* Advanced Socket Selection */}
              {spaceId && (
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <AdvancedSocketSelection
                    integration={integrationRegistry.custom_gpt_action}
                    spaceId={spaceId}
                    currentSocket={selectedSocket}
                    onSocketChange={setSelectedSocket}
                    disabled={isCreating || socketLoading}
                  />
                  
                  {/* Socket Loading State */}
                  {socketLoading && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-3">
                      <div className="flex items-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                      <p className="text-blue-800 text-sm">Setting up socket...</p>
                      </div>
                    </div>
                  )}

                  {/* Socket Error State */}
                  {socketError && !socketLoading && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3 mt-3">
                      <div className="flex items-start gap-2">
                        <AlertCircle className="w-4 h-4 text-red-600 mt-0.5" />
                        <div>
                          <p className="text-red-800 text-sm font-medium">Failed to load socket</p>
                          <p className="text-red-700 text-xs mt-1">{socketError}</p>
                          <p className="text-red-700 text-xs mt-1">You can create a socket manually in the advanced section above.</p>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Socket Info Display */}
                  {selectedSocket && !socketLoading && (
                    <>
                      {/* Template compatibility warning */}
                      {!isCompatibleTemplate && (
                        <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                          <div className="flex items-start space-x-2">
                            <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                            <div>
                              <h4 className="text-amber-900 font-medium text-sm">Template Compatibility Issue</h4>
                              <p className="text-amber-700 text-sm mt-1">
                                This socket uses the <span className="font-medium">{socketTemplateId}</span> template, which is not optimized for Custom GPT integrations. 
                                Consider creating a new socket with the <span className="font-medium">MCP Integration</span> template for better compatibility.
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {/* Template compatibility success */}
                      {isCompatibleTemplate && socketTemplateId === 'mcp' && (
                        <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                          <div className="flex items-start space-x-2">
                            <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                            <div>
                              <h4 className="text-green-900 font-medium text-sm">Perfect Template Match</h4>
                              <p className="text-green-700 text-sm mt-1">
                                This socket uses the MCP Integration template, which is optimized for AI agent integrations like Custom GPTs.
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              </DialogScrollArea>
              <DialogFooter variant="scrollable" className="gap-3 px-6 pb-6">
                <Button
                  type="button"
                  variant="outline"
                  onClick={onClose}
                  className="border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={!integrationName.trim() || isCreating || !selectedSocket || socketLoading}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white min-w-[140px]"
                >
                  {isCreating ? (
                    <>
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                        className="w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2"
                      />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Plus className="w-4 h-4 mr-2" />
                      Create Integration
                    </>
                  )}
                </Button>
              </DialogFooter>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
} 